/**
 * Shared dismiss policy for overlay primitives (#2117).
 *
 * Radix closes an overlay on an outside interaction and on Escape by default. That default is
 * wrong for anything holding unsaved user input: the content unmounts, form state dies with it,
 * and a stray backdrop click silently costs the user everything they typed.
 *
 * Before this existed, every such dialog hand-rolled `onInteractOutside={(e) => e.preventDefault()}`
 * — a dozen copies of the same line, with no shared name for the intent and nowhere to change the
 * mechanism once.
 *
 * WHICH EVENT COUNTS AS "DISMISS": a real pointer-down on the backdrop, or Escape — never focus
 * movement. Radix's `onInteractOutside` fires for pointer-down-outside AND focus-outside, so a
 * dialog wired to it reacts when ANY child overlay (picker, popover, preview, confirm) opens and
 * takes focus. That is how the SmallApp editor came to overwrite the "Apply Preset" prompt with
 * its own discard prompt before the user had clicked anything (#2117). `closeOnOutsideClick`
 * therefore governs `onPointerDownOutside`; focus-outside is left to Radix, which does not
 * dismiss a modal, focus-trapped dialog on its own.
 *
 * `Dialog` consumes this today. `Sheet`, `Drawer` and `AlertDialog` have the same Radix defaults
 * and the same failure mode; they can adopt it by spreading `DismissPolicyProps` into their own
 * props and composing the same two handlers.
 *
 * WHY THIS LIVES IN `components/` AND NOT `lib/`: it is a cross-component BEHAVIOURAL concern for
 * the overlay family, which is the same category as its neighbour `overlay-context.tsx` — not a
 * domain-agnostic helper like `lib/utils.ts`'s `cn` / `parseNumberOnly`. Being a pure function is a
 * property of the implementation, not of the concern; organise by concern. (It started in `lib/`
 * for exactly that bad reason and was moved.)
 *
 * `dismiss` is Radix's own vocabulary, not an invention: `DialogContent`'s props extend
 * `DismissableLayerProps`, and Radix exposes `DismissableLayer` / `onDismiss`.
 */

export interface DismissPolicyProps {
  /**
   * `false` → an outside click (or focus leaving the overlay) no longer closes it.
   * Default `true`. Set this on anything that holds unsaved user input.
   */
  closeOnOutsideClick?: boolean;
  /**
   * `false` → Escape no longer closes the overlay. Default `true`.
   *
   * Kept SEPARATE from `closeOnOutsideClick` on purpose. An outside click is the accidental
   * gesture and is usually safe to block; Escape is deliberate, and is often the user's only
   * remaining way out — so opting out of it is a second, explicit decision rather than a side
   * effect of the first.
   *
   * Only set this when the overlay renders its own ALWAYS-VISIBLE close affordance. Combined with
   * `hideCloseButton` and a footer whose buttons are conditional, turning this off can leave the
   * user with no exit at all.
   */
  closeOnEscape?: boolean;
}

/** The shape Radix hands its dismiss handlers — only `preventDefault` matters to the policy. */
type DismissEvent = { preventDefault: () => void };

/**
 * Build a dismiss handler that COMPOSES the caller's handler with the policy.
 *
 * The caller's handler runs first and keeps full control — it may carry its own conditional logic
 * (e.g. "ignore interactions coming from my child dialog") and may `preventDefault()` on its own.
 * The blanket policy is applied afterwards only when the caller opted out of closing.
 *
 * Composing rather than replacing is the point: an implementation that simply overwrote
 * `onInteractOutside` whenever the policy prop was set would silently drop handlers that real
 * dialogs depend on.
 *
 * @param callerHandler the consumer's own handler, if any
 * @param allowClose    the policy — `false` means "this event must not close the overlay"
 */
export function composeDismissHandler<E extends DismissEvent>(
  callerHandler: ((event: E) => void) | undefined,
  allowClose: boolean,
): (event: E) => void {
  return (event: E) => {
    callerHandler?.(event);
    if (!allowClose) event.preventDefault();
  };
}
